(() => {
  const CARD_SELECTOR = '.card-wrapper.product-card-wrapper';
  const BADGE_SELECTOR =
    '[data-ds-product-availability-badge], .product-pills-right .sold-out-pill';
  const STATUS_BADGE_SELECTOR = '.sold-out-pill, .preowned-pill, .new-pill, .sale-pill';
  const PRODUCT_SWATCH_SELECTOR = '.swatch-group-selector[swatch-url]';
  const PRODUCT_SWATCH_ITEM_SELECTOR = '.swatch-view-item, .swatch-drop-down-list-item';
  const pendingSwatches = new WeakMap();
  const pendingRequests = new WeakMap();
  const pendingSelectionSyncs = new WeakMap();
  const hoverPreviews = new WeakMap();
  const saleSwatchRequests = new Map();
  const saleVariantIds = new Map();
  const saleSwatchStates = new WeakMap();
  const saleSwatchQueue = [];
  let activeSaleSwatchRequests = 0;
  let lastInteractedCard = null;

  // Deduplicate linked products and limit requests for large swatch groups.
  const runSaleSwatchQueue = () => {
    while (activeSaleSwatchRequests < 4 && saleSwatchQueue.length) {
      activeSaleSwatchRequests++;
      saleSwatchQueue.shift()().finally(() => {
        activeSaleSwatchRequests--;
        runSaleSwatchQueue();
      });
    }
  };

  const hasAvailableSaleSize = (url) => {
    if (!saleSwatchRequests.has(url)) {
      saleSwatchRequests.set(url, new Promise((resolve) => {
        saleSwatchQueue.push(async () => {
          try {
            const response = await fetch(url, {
              headers: { Accept: 'application/json' },
              credentials: 'same-origin',
            });
            if (!response.ok) throw new Error('Product unavailable');
            const product = await response.json();
            const saleVariant = Array.isArray(product.variants) && product.variants.find(
              (variant) => variant.available &&
                Number(variant.compare_at_price) > Number(variant.price)
            );
            if (saleVariant) saleVariantIds.set(url, saleVariant.id);
            resolve(Boolean(saleVariant));
          } catch (_error) {
            // Only display options whose sale availability can be confirmed.
            resolve(false);
          }
        });
      }));
      runSaleSwatchQueue();
    }
    return saleSwatchRequests.get(url);
  };

  const filterSaleSwatches = (card) => {
    if (!card?.closest('[data-ds-sale-swatches]')) return;
    card.querySelectorAll(PRODUCT_SWATCH_SELECTOR).forEach((swatch) => {
      const url = getProductJsonUrl(swatch.getAttribute('swatch-url'));
      const item = swatch.closest(PRODUCT_SWATCH_ITEM_SELECTOR) || swatch;
      const previous = saleSwatchStates.get(swatch);
      if (previous?.url === url && previous.item === item) return;
      const state = { url, item };
      saleSwatchStates.set(swatch, state);
      item.setAttribute('data-ds-sale-swatch-hidden', '');
      if (!url) return;
      hasAvailableSaleSize(url).then((eligible) => {
        if (saleSwatchStates.get(swatch) !== state || !swatch.isConnected) return;
        item.toggleAttribute('data-ds-sale-swatch-hidden', !eligible);
        syncSaleProductLinks(card);
      });
    });
  };

  const getCard = (target) => {
    if (!(target instanceof Element)) return null;
    return target.matches(CARD_SELECTOR) ? target : target.closest(CARD_SELECTOR);
  };

  const getProductHandle = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      const productPath = url.pathname.split('/products/')[1] || '';
      return productPath.split('/')[0].replace(/\.js$/i, '');
    } catch (_error) {
      return '';
    }
  };

  const getProductJsonUrl = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      const productPathIndex = url.pathname.indexOf('/products/');
      const handle = getProductHandle(url.href);

      if (url.origin !== window.location.origin || productPathIndex === -1 || !handle) return '';

      const localeRoot = url.pathname.slice(0, productPathIndex);
      url.pathname = `${localeRoot}/products/${handle}.js`;
      url.search = '';
      url.hash = '';
      return url.href;
    } catch (_error) {
      return '';
    }
  };

  const getCurrentProductLink = (card) =>
    card.querySelector(
      '.card__heading a[href*="/products/"], .card__media-link[href*="/products/"]'
    );

  const syncSaleProductLinks = (card) => {
    if (!card?.closest('[data-ds-sale-swatches]')) return;
    card.querySelectorAll('.card__heading a[href], .card__media-link[href]').forEach((link) => {
      const variantId = saleVariantIds.get(getProductJsonUrl(link.href));
      if (!variantId) return;
      const url = new URL(link.href, window.location.origin);
      if (url.searchParams.get('variant') === String(variantId)) return;
      url.searchParams.set('variant', variantId);
      link.href = url.href;
    });
  };

  const findProductSwatch = (card, handle) => {
    const swatches = Array.from(card.querySelectorAll(PRODUCT_SWATCH_SELECTOR));
    const matchingSwatches = handle
      ? swatches.filter((swatch) => getProductHandle(swatch.getAttribute('swatch-url')) === handle)
      : swatches;

    return (
      matchingSwatches.find((swatch) => swatch.classList.contains('swatch-selected')) ||
      matchingSwatches.find((swatch) => swatch.hasAttribute('current-product')) ||
      matchingSwatches[0] ||
      null
    );
  };

  const isUnavailableSwatch = (swatch) => {
    if (!swatch) return null;

    const swatchItem = swatch.closest('.swatch-view-item, .swatch-drop-down-list-item');
    return Boolean(
      swatch.classList.contains('swatch-item-unavailable') ||
        swatchItem?.classList.contains('swatch-item-unavailable') ||
        /sold out/i.test(swatch.getAttribute('aria-label') || '') ||
        /sold out/i.test(swatchItem?.getAttribute('aria-label') || '')
    );
  };

  const setSoldOutBadge = (card, soldOut) => {
    const badges = Array.from(card.querySelectorAll(BADGE_SELECTOR));

    if (!soldOut) {
      badges.forEach((badge) => badge.remove());
      card.dataset.dsSelectedProductSoldOut = 'false';
      return;
    }

    if (!badges.length) {
      const imageArea = card.querySelector('.card__inner');
      if (!imageArea) return;

      let badgeLayer = imageArea.querySelector(':scope > .product-pills-right');

      if (!badgeLayer) {
        badgeLayer = document.createElement('div');
        badgeLayer.className = 'product-pills-right';
        imageArea.appendChild(badgeLayer);
      }

      const badge = document.createElement('span');
      badge.className = 'sold-out-pill';
      badge.dataset.dsProductAvailabilityBadge = '';
      badge.textContent = 'Sold Out';
      badgeLayer.appendChild(badge);
    }

    card.dataset.dsSelectedProductSoldOut = 'true';
  };

  const productIsAvailable = (product) => {
    if (typeof product?.available === 'boolean') return product.available;
    if (!Array.isArray(product?.variants)) return null;
    return product.variants.some((variant) => variant.available);
  };

  const getBadgeKind = (badge) => {
    if (badge.classList.contains('sold-out-pill')) return 'sold-out';
    if (badge.classList.contains('preowned-pill')) return 'preowned';
    if (badge.classList.contains('new-pill')) return 'new';
    if (badge.classList.contains('sale-pill')) return 'sale';
    return '';
  };

  const getHoveredBadgeKinds = (swatch, product = null) => {
    const kinds = new Set();
    const productUrl = swatch.getAttribute('swatch-url') || '';
    const descriptor = [
      getProductHandle(productUrl),
      product?.handle,
      product?.title,
      ...(Array.isArray(product?.tags) ? product.tags : []),
    ]
      .filter(Boolean)
      .join(' ');
    const swatchUnavailable = isUnavailableSwatch(swatch);
    const available =
      swatchUnavailable === true ? false : product ? productIsAvailable(product) : true;
    const isPreowned = /pre[\s-]?owned/i.test(descriptor);

    // Match the card badge priority: sold out overrides every merchandising
    // badge, followed by pre-owned, sale, and finally new arrival.
    if (available === false) {
      kinds.add('sold-out');
      return kinds;
    }

    if (isPreowned) {
      kinds.add('preowned');
      return kinds;
    }

    if (
      Array.isArray(product?.variants) &&
      product.variants.some(
        (variant) =>
          Number(variant.compare_at_price) > 0 &&
          Number(variant.compare_at_price) > Number(variant.price)
      )
    ) {
      kinds.add('sale');
      return kinds;
    }

    if (product) {
      const createdAt = Date.parse(product.created_at || '');
      const productAge = Date.now() - createdAt;
      const newProductWindow = 30 * 24 * 60 * 60 * 1000;

      if (Number.isFinite(createdAt) && productAge >= 0 && productAge <= newProductWindow) {
        kinds.add('new');
      }
    }

    return kinds;
  };

  const isSelectedSwatch = (swatch) => {
    if (!swatch) return false;

    const swatchItem = swatch.closest(PRODUCT_SWATCH_ITEM_SELECTOR);
    return Boolean(
      swatch.classList.contains('swatch-selected') ||
        swatch.hasAttribute('current-product') ||
        swatchItem?.getAttribute('aria-checked') === 'true' ||
        swatchItem?.getAttribute('aria-selected') === 'true'
    );
  };

  const createHoverBadge = (imageArea, kind) => {
    const badge = document.createElement('span');
    const badgeDetails = {
      'sold-out': ['sold-out-pill', 'Sold Out'],
      preowned: ['preowned-pill', 'PRE-OWNED'],
      new: ['new-pill', 'New Arrival'],
      sale: ['sale-pill', 'Sale'],
    }[kind];

    if (!badgeDetails) return null;

    badge.className = badgeDetails[0];
    badge.textContent = badgeDetails[1];
    badge.dataset.dsHoverProductBadge = '';

    if (kind === 'sold-out') badge.dataset.dsProductAvailabilityBadge = '';

    if (kind === 'sold-out' || kind === 'sale') {
      let badgeLayer = imageArea.querySelector(':scope > .product-pills-right');

      if (!badgeLayer) {
        badgeLayer = document.createElement('div');
        badgeLayer.className = 'product-pills-right';
        imageArea.appendChild(badgeLayer);
      }

      badgeLayer.appendChild(badge);
    } else {
      imageArea.prepend(badge);
    }

    return badge;
  };

  const setSelectedProductBadge = (card, kinds) => {
    const imageArea = card.querySelector('.card__inner');
    if (!imageArea) return;

    imageArea.querySelectorAll(STATUS_BADGE_SELECTOR).forEach((badge) => badge.remove());

    const kind = kinds.values().next().value || '';
    const badge = kind ? createHoverBadge(imageArea, kind) : null;

    if (badge) delete badge.dataset.dsHoverProductBadge;
    card.dataset.dsSelectedProductSoldOut = String(kind === 'sold-out');
  };

  const applyHoverBadges = (card, preview, kinds) => {
    if (hoverPreviews.get(card) !== preview || !card.isConnected) return;

    preview.createdBadges.forEach((badge) => badge.remove());
    preview.createdBadges = [];

    preview.originalBadges.forEach(({ badge }) => {
      if (!badge.isConnected) return;
      badge.hidden = true;
      badge.setAttribute('aria-hidden', 'true');
    });

    kinds.forEach((kind) => {
      const originalBadge = preview.originalBadges.find(
        ({ badge }) => badge.isConnected && getBadgeKind(badge) === kind
      )?.badge;
      const badge = originalBadge || createHoverBadge(preview.imageArea, kind);

      if (!badge) return;
      if (!originalBadge) preview.createdBadges.push(badge);
      badge.hidden = false;
      badge.setAttribute('aria-hidden', 'false');
    });

    card.dataset.dsHoverProductBadges = 'true';
    card.dataset.dsHoverProductSoldOut = String(kinds.has('sold-out'));
  };

  const restoreHoverPreview = (card) => {
    const preview = hoverPreviews.get(card);
    if (!preview) return;

    preview.createdBadges.forEach((badge) => badge.remove());
    preview.originalBadges.forEach(({ badge, hidden, ariaHidden }) => {
      if (!badge.isConnected) return;
      badge.hidden = hidden;

      if (ariaHidden === null) {
        badge.removeAttribute('aria-hidden');
      } else {
        badge.setAttribute('aria-hidden', ariaHidden);
      }
    });

    delete card.dataset.dsHoverProductBadges;
    delete card.dataset.dsHoverProductSoldOut;
    hoverPreviews.delete(card);
  };

  const verifyHoveredProduct = async (card, preview, swatch) => {
    const productJsonUrl = getProductJsonUrl(swatch.getAttribute('swatch-url'));
    if (!productJsonUrl) return;

    try {
      const response = await fetch(productJsonUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Product request failed with ${response.status}`);

      const product = await response.json();
      applyHoverBadges(card, preview, getHoveredBadgeKinds(swatch, product));
    } catch (_error) {
      // The availability class on the swatch already supplies the essential
      // sold-out state when optional product details cannot be loaded.
    }
  };

  const beginHoverPreview = (card, swatch) => {
    if (!card?.isConnected || !swatch) return;

    // The card already represents this product, so hovering its selected
    // swatch must not replace or hide the current Liquid/app badge.
    if (isSelectedSwatch(swatch)) {
      restoreHoverPreview(card);
      return;
    }

    const activePreview = hoverPreviews.get(card);
    if (activePreview?.swatch === swatch) return;
    if (activePreview) restoreHoverPreview(card);

    const imageArea = card.querySelector('.card__inner');
    if (!imageArea) return;

    const preview = {
      swatch,
      imageArea,
      createdBadges: [],
      originalBadges: Array.from(imageArea.querySelectorAll(STATUS_BADGE_SELECTOR)).map(
        (badge) => ({
          badge,
          hidden: badge.hidden,
          ariaHidden: badge.getAttribute('aria-hidden'),
        })
      ),
    };

    hoverPreviews.set(card, preview);
    applyHoverBadges(card, preview, getHoveredBadgeKinds(swatch));
    verifyHoveredProduct(card, preview, swatch);
  };

  const getSwatchInteraction = (target) => {
    if (!(target instanceof Element)) return {};

    const directSwatch = target.closest(PRODUCT_SWATCH_SELECTOR);
    const item =
      directSwatch?.closest(PRODUCT_SWATCH_ITEM_SELECTOR) ||
      target.closest(PRODUCT_SWATCH_ITEM_SELECTOR);
    const swatch = directSwatch || item?.querySelector(PRODUCT_SWATCH_SELECTOR);

    return { item: item || swatch, swatch };
  };

  const verifyProductAvailability = async (card, productUrl) => {
    const productJsonUrl = getProductJsonUrl(productUrl);
    if (!productJsonUrl) return;

    const requestToken = {};
    pendingRequests.set(card, requestToken);

    try {
      const response = await fetch(productJsonUrl, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Product request failed with ${response.status}`);

      const product = await response.json();
      const available = productIsAvailable(product);

      if (pendingRequests.get(card) !== requestToken || !card.isConnected || available === null) return;

      const saleVariant = product.variants?.find((variant) =>
        variant.available && Number(variant.compare_at_price) > Number(variant.price)
      );
      if (saleVariant) {
        saleVariantIds.set(productJsonUrl, saleVariant.id);
        syncSaleProductLinks(card);
      }

      const productHandle = getProductHandle(productUrl);
      const swatch = findProductSwatch(card, productHandle);

      if (swatch) {
        setSelectedProductBadge(card, getHoveredBadgeKinds(swatch, product));
      } else {
        setSoldOutBadge(card, !available);
      }
    } catch (_error) {
      // Keep the immediate availability state supplied by SA Variants if the
      // storefront product endpoint is temporarily unavailable.
    }
  };

  const syncSelectedSwatch = (card, swatch) => {
    if (!card?.isConnected || !swatch) return;

    const productUrl = swatch.getAttribute('swatch-url');

    setSelectedProductBadge(card, getHoveredBadgeKinds(swatch));
    verifyProductAvailability(card, productUrl);
  };

  const syncChangedCard = (card) => {
    if (!card?.isConnected) return;

    const currentLink = getCurrentProductLink(card);
    const currentHandle = getProductHandle(currentLink?.href);
    const matchingSwatch = findProductSwatch(card, currentHandle);
    const rememberedSwatch = pendingSwatches.get(card);
    const swatch = matchingSwatch || rememberedSwatch;
    const productUrl = swatch?.getAttribute('swatch-url') || currentLink?.href;

    pendingSwatches.delete(card);

    if (swatch) setSelectedProductBadge(card, getHoveredBadgeKinds(swatch));
    verifyProductAvailability(card, productUrl);
  };

  const rememberInteractedSwatch = (event) => {
    const { swatch } = getSwatchInteraction(event.target);
    const card = getCard(swatch);

    if (swatch && card) {
      restoreHoverPreview(card);
      lastInteractedCard = card;
      pendingSwatches.set(card, swatch);
      syncSelectedSwatch(card, swatch);

      const productHandle = getProductHandle(swatch.getAttribute('swatch-url'));
      const selectionToken = {};
      pendingSelectionSyncs.set(card, selectionToken);

      const syncAfterSaUpdate = () => {
        if (
          pendingSelectionSyncs.get(card) !== selectionToken ||
          !card.isConnected
        ) {
          return;
        }

        const updatedSwatch = findProductSwatch(card, productHandle) || findProductSwatch(card, '');
        if (updatedSwatch) syncSelectedSwatch(card, updatedSwatch);
      };

      requestAnimationFrame(syncAfterSaUpdate);
      window.setTimeout(syncAfterSaUpdate, 150);
    }
  };

  document.addEventListener(
    'mouseover',
    (event) => {
      const { item, swatch } = getSwatchInteraction(event.target);
      const card = getCard(swatch);

      if (!item || !swatch || !card) return;
      if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
      beginHoverPreview(card, swatch);
    },
    true
  );

  document.addEventListener(
    'mouseout',
    (event) => {
      const { item, swatch } = getSwatchInteraction(event.target);
      const card = getCard(swatch);

      if (!item || !swatch || !card) return;
      if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;

      const preview = hoverPreviews.get(card);
      if (preview?.swatch === swatch) restoreHoverPreview(card);
    },
    true
  );

  document.addEventListener('click', rememberInteractedSwatch, true);
  document.addEventListener('change', rememberInteractedSwatch, true);
  document.addEventListener(
    'keyup',
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') rememberInteractedSwatch(event);
    },
    true
  );

  document.addEventListener('vkcl:product:changed', (event) => {
    const card = getCard(event.target) || lastInteractedCard;
    if (!card) return;

    // SA Variants dispatches its event just before it finishes updating the
    // card links, so wait until that synchronous update has completed.
    requestAnimationFrame(() => syncChangedCard(card));
  });

  const initializeVariantCard = (card) => {
    filterSaleSwatches(card);
    syncSaleProductLinks(card);
    if (!card || card.dataset.dsSaAvailabilityInitialized === 'true') return;

    const selectedSwatch = findProductSwatch(card, '');
    if (!selectedSwatch) return;

    card.dataset.dsSaAvailabilityInitialized = 'true';
    syncSelectedSwatch(card, selectedSwatch);
  };

  const initializeVariantCards = (root) => {
    if (root instanceof Element) initializeVariantCard(root.closest(CARD_SELECTOR));
    root.querySelectorAll?.(CARD_SELECTOR).forEach(initializeVariantCard);
  };

  initializeVariantCards(document);

  new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === 'attributes') initializeVariantCards(record.target);
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) initializeVariantCards(node);
      });
    });
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['swatch-url', 'href'],
  });
})();
